<template>
  <b-modal
    :id="modalId"
    :title="event ? event.title : 'Governance Event'"
    :size="event && event.can_manage ? 'xl' : 'lg'"
    hide-footer
    @hidden="$emit('hidden')"
  >
    <b-overlay :show="loading" rounded="sm" no-fade>
      <div v-if="event">
        <b-row class="mb-1">
          <b-col cols="12" md="6">
            <p class="mb-50"><strong>When:</strong> {{ govFormatRange(event) }}</p>
            <p class="mb-50"><strong>Type:</strong> {{ govTypeLabel(event.event_type) }}</p>
            <p class="mb-50">
              <strong>Status:</strong>
              <b-badge :variant="govStatusVariant(event.status)" class="text-capitalize">{{ event.status }}</b-badge>
            </p>
            <p class="mb-50" v-if="event.working_group">
              <strong>Working group:</strong> {{ event.working_group.group_name }}
            </p>
          </b-col>
          <b-col cols="12" md="6">
            <p class="mb-50" v-if="event.creator">
              <strong>Organiser:</strong> {{ event.creator.name }} {{ event.creator.surname }}
            </p>
            <p class="mb-50" v-if="event.cancelled_reason">
              <strong>Cancelled:</strong> {{ event.cancelled_reason }}
            </p>
          </b-col>
        </b-row>

        <div class="mb-1">
          <strong class="d-block mb-50">Parts</strong>
          <governance-event-parts :event="event" compact />
        </div>

        <div v-if="event.description" class="mb-1 border rounded p-1" style="white-space: pre-wrap;">{{ event.description }}</div>

        <!-- My response -->
        <div v-if="event.my_response" class="mb-1 d-flex align-items-center flex-wrap">
          <strong class="mr-1">My response:</strong>
          <b-badge :variant="govResponseVariant(event.my_response)" class="text-capitalize mr-1">{{ event.my_response }}</b-badge>
          <template v-if="event.status === 'scheduled'">
            <b-button size="sm" variant="outline-success" class="mr-50" :disabled="responding" @click="respond('accepted')">Accept</b-button>
            <b-button size="sm" variant="outline-warning" class="mr-50" :disabled="responding" @click="respond('tentative')">Tentative</b-button>
            <b-button size="sm" variant="outline-danger" :disabled="responding" @click="respond('declined')">Decline</b-button>
          </template>
        </div>

        <!-- Materials -->
        <div v-if="event.materials && event.materials.length" class="mb-1">
          <strong>Agenda, minutes and materials</strong>
          <b-list-group class="mt-50">
            <b-list-group-item
              v-for="m in event.materials"
              :key="m.id"
              class="d-flex justify-content-between align-items-center py-50"
            >
              <span>
                <b-badge variant="light-primary" class="text-capitalize mr-50">{{ m.kind }}</b-badge>
                {{ m.title }}
                <small class="text-muted" v-if="m.file_name">({{ m.file_name }})</small>
              </span>
              <b-button size="sm" variant="outline-primary" @click="govDownloadMaterial(m)">
                <feather-icon :icon="m.external_url ? 'ExternalLinkIcon' : 'DownloadIcon'" size="14" />
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </div>

        <!-- My special requirements (invitees) -->
        <div v-if="event.my_response && event.status !== 'cancelled'" class="mb-1 border rounded p-1">
          <label class="font-weight-bold mb-25" for="gov-special-needs">
            Please state any additional requirements (including food allergies)
          </label>
          <b-form-textarea
            id="gov-special-needs"
            v-model="specialNeeds"
            rows="2"
            max-rows="6"
            maxlength="2000"
            placeholder="Enter your additional requirements here..."
          />
          <div class="d-flex justify-content-between align-items-center mt-50">
            <small class="text-muted">Only the Event administrator can see this.</small>
            <b-button size="sm" variant="primary" :disabled="savingNeeds || specialNeeds === (event.my_special_needs || '')" @click="saveRequirements">
              Save requirements
            </b-button>
          </div>
        </div>

        <!-- Event administrator: responses, special requirements and attendance -->
        <div v-if="event.can_manage && event.invitees && event.invitees.length" class="mb-1">
          <div class="d-flex justify-content-between align-items-center mb-50">
            <strong>Invitees ({{ event.invitees.length }})</strong>
            <b-button
              v-if="['scheduled', 'completed'].includes(event.status)"
              size="sm"
              variant="outline-primary"
              :disabled="sendingAttendance"
              @click="sendAttendanceEmails"
            >
              <feather-icon icon="MailIcon" class="mr-25" />Send attendance confirmation
            </b-button>
          </div>
          <b-table :items="event.invitees" :fields="manageFields" small bordered responsive class="mb-0">
            <template #cell(name)="{ item }">{{ item.user ? `${item.user.name} ${item.user.surname}` : item.user_id }}</template>
            <template #cell(response)="{ item }">
              <b-badge :variant="govResponseVariant(item.response)" class="text-capitalize">{{ item.response }}</b-badge>
            </template>
            <template #cell(special_needs)="{ item }">
              <span v-if="item.special_needs" style="white-space: pre-wrap;">{{ item.special_needs }}</span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(attended)="{ item }">
              <b-form-select
                :value="item.attended"
                :options="attendanceOptions"
                size="sm"
                :disabled="savingAttendance"
                @change="(val) => setAttended(item, val)"
              />
              <small v-if="item.attended !== null && item.attendance_source" class="d-block text-muted">
                {{ item.attendance_source === "self" ? "Confirmed by invitee" : "Recorded by organiser" }}
              </small>
            </template>
          </b-table>
        </div>

        <!-- Invitees (full governance users only) -->
        <div v-else-if="event.invitees && event.invitees.length && showInvitees" class="mb-1">
          <strong>Invitees ({{ event.invitees.length }})</strong>
          <div class="mt-50">
            <b-badge
              v-for="inv in event.invitees"
              :key="inv.id"
              :variant="govResponseVariant(inv.response)"
              class="mr-50 mb-50"
              :title="inv.response"
            >
              {{ inv.user ? `${inv.user.name} ${inv.user.surname}` : inv.user_id }}
            </b-badge>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <b-button
            v-if="showOpen && $Can('view_governance_events')"
            size="sm"
            variant="primary"
            class="mr-50"
            :to="{ name: 'governance-event-view', params: { id: event.id } }"
          >
            Open
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="$bvModal.hide(modalId)">Close</b-button>
        </div>
      </div>
      <div v-else class="text-muted">Loading...</div>
    </b-overlay>
  </b-modal>
</template>

<script>
import store from "@/store";
import governanceMixin, { registerGovernanceStore } from "./governanceMixin";
import GovernanceEventParts from "./GovernanceEventParts.vue";

export default {
  name: "GovernanceEventDetailsModal",
  components: { GovernanceEventParts },
  mixins: [governanceMixin],
  props: {
    modalId: { type: String, default: "governance-event-details" },
    eventId: { type: [Number, String], default: null },
    showInvitees: { type: Boolean, default: true },
    showOpen: { type: Boolean, default: true },
  },
  data() {
    return {
      event: null,
      loading: false,
      responding: false,
      specialNeeds: "",
      savingNeeds: false,
      savingAttendance: false,
      sendingAttendance: false,
      attendanceOptions: [
        { value: null, text: "Not recorded" },
        { value: true, text: "Attended" },
        { value: false, text: "Did not attend" },
      ],
    };
  },
  computed: {
    /** Special requirements only for the Event administrator (and admins). */
    manageFields() {
      const fields = [{ key: "name", label: "Invitee" }, { key: "response", label: "Response" }];
      if (this.event && this.event.can_see_requirements) fields.push({ key: "special_needs", label: "Special requirements" });
      fields.push({ key: "attended", label: "Attended" });
      return fields;
    },
    meetingHref() {
      const url = this.event && this.event.meeting_url;
      if (!url) return "#";
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },
  },
  watch: {
    eventId: {
      immediate: true,
      handler(id) {
        if (id) this.load(id);
        else this.event = null;
      },
    },
  },
  created() {
    registerGovernanceStore();
  },
  methods: {
    load(id) {
      this.loading = true;
      store
        .dispatch("governance/getEvent", id)
        .then((res) => {
          this.event = res.data;
          this.specialNeeds = res.data.my_special_needs || "";
        })
        .catch((error) => {
          this.event = null;
          this.govToast("Could not load event", "danger", this.govErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    respond(response) {
      this.responding = true;
      store
        .dispatch("governance/respond", { id: this.event.id, response })
        .then(() => {
          this.event.my_response = response;
          this.$emit("responded", { id: this.event.id, response });
          this.govToast("Response saved");
        })
        .catch((error) => this.govToast("Could not save response", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.responding = false;
        });
    },
    saveRequirements() {
      this.savingNeeds = true;
      store
        .dispatch("governance/updateRequirements", { id: this.event.id, special_needs: this.specialNeeds })
        .then((res) => {
          this.event.my_special_needs = res.data.special_needs;
          this.specialNeeds = res.data.special_needs || "";
          this.$emit("requirements-saved", { id: this.event.id, special_needs: res.data.special_needs });
          this.govToast("Your additional requirements have been saved");
        })
        .catch((error) => this.govToast("Could not save requirements", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.savingNeeds = false;
        });
    },
    setAttended(invitee, value) {
      this.savingAttendance = true;
      store
        .dispatch("governance/setAttendance", { id: this.event.id, attendance: [{ user_id: invitee.user_id, attended: value }] })
        .then((res) => {
          const row = (res.data || []).find((r) => r.user_id === invitee.user_id);
          if (row) Object.assign(invitee, { attended: row.attended, attendance_source: row.attendance_source });
        })
        .catch((error) => this.govToast("Could not save attendance", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.savingAttendance = false;
        });
    },
    sendAttendanceEmails() {
      this.sendingAttendance = true;
      store
        .dispatch("governance/sendAttendanceEmails", { id: this.event.id })
        .then((res) => this.govToast(res.data.message, res.data.sent ? "success" : "warning"))
        .catch((error) => this.govToast("Could not send emails", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.sendingAttendance = false;
        });
    },
  },
};
</script>
